/**
 * Settings — v4.0 Smart Settings Center
 * 5 tabs: Connections, Notifications, Mode, AI, Risk
 */
import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import TotpModal from "@/components/TotpModal";

// ─── Types ───────────────────────────────────────────────────────────────────

interface Settings {
  broker: string;
  apiKey: string;
  apiSecret: string;
  totpSecret: string;
  defaultMode: string;
  mode: string;
  maxPositionSize: string;
  stopLossPct: string;
  riskPerTrade: string;
  autoTrade: boolean;
  notifications: boolean;
  slackWebhook: string;
  telegramBotToken: string;
  telegramChatId: string;
  zaiApiKey: string;
  minimaxApiKey: string;
  aiEnabled: boolean;
  aiConfirmOrders: boolean;
  aiVetoSignals: boolean;
  aiNewsSentiment: boolean;
  aiWeeklyReview: boolean;
  aiMonthlyPatches: boolean;
  aiAutoSize: boolean;
  aiModelTriage: string;
  aiModelReasoning: string;
  aiModelVeto: string;
  aiModelBulk: string;
  aiDailyBudget: string;
  aiBudgetPause: boolean;
  guardMaxPosSize: string;
  guardSectorConcentration: string;
  guardDailyLoss: string;
  guardWeeklyLoss: string;
  guardMaxDrawdown: string;
  guardConsecutiveLosses: string;
  sizingRiskPerTrade: string;
  sizingStopLossType: string;
  sizingDefaultTarget: string;
  notificationEvents: Record<string, boolean>;
  [key: string]: unknown;
}

type Tab = "connections" | "notifications" | "mode" | "ai" | "risk";

interface TestResult { success: boolean; message: string; }

const EVENT_LABELS: Record<string, string> = {
  trade_placed: "Trade placed",
  trade_filled: "Trade filled",
  stop_loss_hit: "Stop loss hit",
  target_hit: "Target hit",
  guardrail_breach: "Guardrail breach",
  autohalt_triggered: "AutoHalt triggered",
  daily_pnl: "Daily P&L summary (16:00)",
  weekly_review: "Weekly review (Sat 10:00)",
  ai_veto: "AI veto issued",
  routine_failed: "Routine failed",
};

// ─── Sub-components ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16, borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
      <span style={{ width: 180, fontSize: 13, color: "var(--text-muted)", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function Input({ value, onChange, type = "text", placeholder, style }: any) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        background: "var(--bg)",
        border: "1px solid var(--border)",
        borderRadius: 6,
        padding: "6px 10px",
        color: "var(--text)",
        fontSize: 13,
        width: "100%",
        maxWidth: 400,
        ...style,
      }}
    />
  );
}

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: 40,
        height: 22,
        borderRadius: 11,
        background: checked ? "#6c5ce7" : "var(--border)",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        position: "relative",
        transition: "background 0.2s",
        flexShrink: 0,
      }}
    >
      <div style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "white",
        position: "absolute",
        top: 2,
        left: checked ? 20 : 2,
        transition: "left 0.2s",
      }} />
    </button>
  );
}

function TestButton({ label, onClick, loading, result }: { label: string; onClick: () => void; loading?: boolean; result?: TestResult }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button
        onClick={onClick}
        disabled={loading}
        style={{
          background: "transparent",
          border: "1px solid var(--border)",
          borderRadius: 6,
          color: "var(--text)",
          padding: "4px 12px",
          fontSize: 12,
          cursor: loading ? "wait" : "pointer",
          opacity: loading ? 0.6 : 1,
        }}
      >
        {loading ? "Testing..." : label}
      </button>
      {result && (
        <span style={{
          fontSize: 12,
          color: result.success ? "#00b894" : "#e17055",
          maxWidth: 300,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {result.message}
        </span>
      )}
    </div>
  );
}

function SaveButton({ onClick, loading, saved }: { onClick: () => void; loading?: boolean; saved?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        background: saved ? "#00b894" : "var(--primary-light)",
        border: "none",
        borderRadius: 6,
        color: "white",
        padding: "8px 20px",
        fontSize: 13,
        fontWeight: 600,
        cursor: loading ? "wait" : "pointer",
        opacity: loading ? 0.7 : 1,
        transition: "background 0.2s",
      }}
    >
      {loading ? "Saving..." : saved ? "Saved" : "Save"}
    </button>
  );
}

// ─── Tab Components ──────────────────────────────────────────────────────────

function ConnectionsTab({ settings, setSettings, testResults, runTest }: {
  settings: Settings;
  setSettings: (s: Settings) => void;
  testResults: Record<string, TestResult>;
  runTest: (key: string, endpoint: string) => void;
}) {
  const update = (key: string, value: unknown) => setSettings({ ...settings, [key]: value });

  return (
    <div>
      <Section title="Zerodha Kite">
        <FieldRow label="API Key">
          <Input value={settings.apiKey} onChange={(e: any) => update("apiKey", e.target.value)} placeholder="e.g. 7l6nnau72thwcdlk" />
        </FieldRow>
        <FieldRow label="API Secret">
          <Input type="password" value={settings.apiSecret} onChange={(e: any) => update("apiSecret", e.target.value)} placeholder="••••••••••••••••••••" />
        </FieldRow>
        <FieldRow label="Client ID">
          <Input value={settings.broker === "zerodha" ? "IRQ498" : ""} onChange={() => {}} placeholder="e.g. IRQ498" />
        </FieldRow>
        <FieldRow label="">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <TestButton label="Test Connection" onClick={() => runTest("zerodha", "/settings/test/zerodha")} result={testResults.zerodha} />
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("bazaar_token");
                  const res = await fetch("/api/v1/kite/auth-url", { headers: { Authorization: `Bearer ${token}` } });
                  const data = await res.json();
                  if (data.url) window.open(data.url, "_blank");
                } catch (e: any) {
                  alert("Failed to get Kite login URL: " + e.message);
                }
              }}
              style={{
                padding: "8px 16px", background: "#6C5CE7", color: "#fff", border: "none",
                borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500,
              }}
            >
              Connect Zerodha
            </button>
          </div>
        </FieldRow>
        <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(0,184,148,0.1)", borderRadius: 6, fontSize: 12, color: "var(--text-muted)" }}>
          <strong style={{ color: "#00b894" }}>Reminder:</strong> Set your Zerodha redirect URL to{" "}
          <code style={{ background: "rgba(255,255,255,0.1)", padding: "1px 4px", borderRadius: 3 }}>
            https://bazaar.dhc.work.gd/api/v1/kite/callback
          </code>
        </div>
      </Section>

      <Section title="AI Providers">
        <FieldRow label="Z.AI API Key">
          <Input type="password" value={settings.zaiApiKey || ""} onChange={(e: any) => update("zaiApiKey", e.target.value)} placeholder="zai_••••••••••••••••••••" />
        </FieldRow>
        <FieldRow label="">
          <TestButton label="Test Z.AI" onClick={() => runTest("zai", "/settings/test/zai")} result={testResults.zai} />
        </FieldRow>
        <FieldRow label="MiniMax API Key">
          <Input type="password" value={settings.minimaxApiKey || ""} onChange={(e: any) => update("minimaxApiKey", e.target.value)} placeholder="••••••••••••••••••••" />
        </FieldRow>
        <FieldRow label="">
          <TestButton label="Test MiniMax" onClick={() => runTest("minimax", "/settings/test/minimax")} result={testResults.minimax} />
        </FieldRow>
      </Section>
    </div>
  );
}

function NotificationsTab({ settings, setSettings, testResults, runTest }: {
  settings: Settings;
  setSettings: (s: Settings) => void;
  testResults: Record<string, TestResult>;
  runTest: (key: string, endpoint: string) => void;
}) {
  const events = settings.notificationEvents || {};

  const toggleEvent = (key: string) => {
    setSettings({ ...settings, notificationEvents: { ...events, [key]: !events[key] } });
  };

  return (
    <div>
      <Section title="Delivery Channels">
        <FieldRow label="Telegram Bot Token">
          <Input type="password" value={settings.telegramBotToken || ""} onChange={(e: any) => setSettings({ ...settings, telegramBotToken: e.target.value })} placeholder="••••••••••••••••••••" />
        </FieldRow>
        <FieldRow label="Telegram Chat ID">
          <Input value={settings.telegramChatId || ""} onChange={(e: any) => setSettings({ ...settings, telegramChatId: e.target.value })} placeholder="1285300022" />
        </FieldRow>
        <FieldRow label="">
          <TestButton label="Test Telegram" onClick={() => runTest("telegram", "/settings/test/telegram")} result={testResults.telegram} />
        </FieldRow>

        <div style={{ height: 16 }} />

        <FieldRow label="Slack Webhook URL">
          <Input value={settings.slackWebhook || ""} onChange={(e: any) => setSettings({ ...settings, slackWebhook: e.target.value })} placeholder="https://hooks.slack.com/..." />
        </FieldRow>
        <FieldRow label="">
          <TestButton label="Test Slack" onClick={() => runTest("slack", "/settings/test/slack")} result={testResults.slack} />
        </FieldRow>
      </Section>

      <Section title="Notify On Events">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
          {Object.entries(EVENT_LABELS).map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 13 }}>
              <Toggle checked={!!events[key]} onChange={() => toggleEvent(key)} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </Section>
    </div>
  );
}

function ModeTab({ settings, setSettings, showTotp, setShowTotp }: {
  settings: Settings;
  setSettings: (s: Settings) => void;
  showTotp: boolean;
  setShowTotp: (v: boolean) => void;
}) {
  const currentMode = settings.mode || "virtual";
  const isVirtual = currentMode === "virtual" || currentMode === "VIRTUAL";
  const [switching, setSwitching] = useState(false);

  const handleSwitch = async (toMode: string) => {
    if (toMode === "real") {
      setShowTotp(true);
      return;
    }
    setSwitching(true);
    try {
      await api.post("/settings/mode/switch", { mode: "virtual" });
      setSettings({ ...settings, mode: "virtual", defaultMode: "VIRTUAL" });
    } catch (e) {
      console.error("Mode switch failed", e);
    } finally {
      setSwitching(false);
    }
  };

  const handleTotpConfirm = async (code: string) => {
    setSwitching(true);
    try {
      await api.post("/settings/mode/switch", { mode: "real", totpCode: code });
      setSettings({ ...settings, mode: "real", defaultMode: "REAL" });
      setShowTotp(false);
    } catch (e) {
      console.error("Real mode switch failed", e);
      alert("TOTP verification failed or mode switch failed.");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div>
      <Section title="Current Mode">
        <div style={{ display: "flex", alignItems: "center", gap: 24, padding: "16px", background: isVirtual ? "rgba(0,184,148,0.1)" : "rgba(225,112,85,0.1)", borderRadius: 8, border: `1px solid ${isVirtual ? "#00b894" : "#e17055"}` }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: isVirtual ? "rgba(0,184,148,0.2)" : "rgba(225,112,85,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>
            {isVirtual ? "V" : "R"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: isVirtual ? "#00b894" : "#e17055" }}>
              {isVirtual ? "VIRTUAL MODE" : "REAL MODE"}
            </div>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
              {isVirtual
                ? "Placing simulated orders against real market prices. No money leaves your account."
                : "Placing real orders via Kite Connect. Real capital at risk."}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
          {isVirtual ? (
            <button
              onClick={() => handleSwitch("real")}
              disabled={switching}
              style={{
                background: "#e17055",
                border: "none",
                borderRadius: 6,
                color: "white",
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: switching ? "wait" : "pointer",
                opacity: switching ? 0.7 : 1,
              }}
            >
              {switching ? "Switching..." : "Switch to REAL Mode"}
            </button>
          ) : (
            <button
              onClick={() => handleSwitch("virtual")}
              disabled={switching}
              style={{
                background: "var(--primary-light)",
                border: "none",
                borderRadius: 6,
                color: "white",
                padding: "10px 24px",
                fontSize: 14,
                fontWeight: 600,
                cursor: switching ? "wait" : "pointer",
                opacity: switching ? 0.7 : 1,
              }}
            >
              {switching ? "Switching..." : "Switch to VIRTUAL Mode"}
            </button>
          )}
        </div>
        {!isVirtual && (
          <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(225,112,85,0.1)", borderRadius: 6, fontSize: 12, color: "#e17055" }}>
            Real mode requires SEBI-compliant algo trading setup. Ensure you have your SEBI_ALGO_ID configured.
          </div>
        )}
      </Section>

      <Section title="Virtual Capital">
        <FieldRow label="Starting Balance">
          <Input type="number" value={settings.maxPositionSize || "1000000"} onChange={(e: any) => setSettings({ ...settings, maxPositionSize: e.target.value })} placeholder="1000000" />
        </FieldRow>
        <FieldRow label="Current NAV">
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--primary-light)" }}>₹ 997,420 (from positions)</span>
        </FieldRow>
      </Section>

      <Section title="Data Source">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[
            { value: "live", label: "Live Kite Ticks", desc: "Use real-time prices from Zerodha Kite WebSocket" },
            { value: "timemachine", label: "Time-Machine Mode", desc: "Replay any historical day (2001–2026)" },
          ].map(opt => (
            <label key={opt.value} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", background: "var(--bg)" }}>
              <input type="radio" name="datasource" value={opt.value} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{opt.label}</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </Section>

      {showTotp && (
        <TotpModal
          open={true}
          onClose={() => setShowTotp(false)}
          onSubmit={handleTotpConfirm}
        />
      )}
    </div>
  );
}

function AITab({ settings, setSettings }: {
  settings: Settings;
  setSettings: (s: Settings) => void;
}) {
  const update = (key: string, value: unknown) => setSettings({ ...settings, [key]: value });

  return (
    <div>
      <Section title="AI Master Switch">
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: settings.aiEnabled ? "rgba(108,92,231,0.1)" : "rgba(255,255,255,0.05)", borderRadius: 8, border: "1px solid var(--border)" }}>
          <Toggle checked={!!settings.aiEnabled} onChange={(v: boolean) => update("aiEnabled", v)} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Enable AI Features</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Controls all AI-powered features across the platform</div>
          </div>
        </div>
      </Section>

      <Section title="Per-Feature Toggles">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
          {[
            ["aiConfirmOrders", "AI confirms every order before placing"],
            ["aiVetoSignals", "AI vetoes signals it doesn't trust"],
            ["aiNewsSentiment", "AI computes news sentiment for held stocks"],
            ["aiWeeklyReview", "AI writes weekly review narrative"],
            ["aiMonthlyPatches", "AI proposes monthly strategy patches"],
            ["aiAutoSize", "AI auto-adjusts position sizes (caution)"],
          ].map(([key, label]) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, cursor: settings.aiEnabled ? "pointer" : "not-allowed", fontSize: 13, opacity: settings.aiEnabled ? 1 : 0.5 }}>
              <Toggle checked={!!(settings as any)[key]} onChange={(v: boolean) => update(key, v)} disabled={!settings.aiEnabled} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </Section>

      <Section title="Model Routing">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            ["aiModelTriage", "Triage", "GLM-4.5-Air"],
            ["aiModelReasoning", "Reasoning", "GLM-4.7"],
            ["aiModelVeto", "Veto", "GLM-5.1"],
            ["aiModelBulk", "Bulk / News", "MiniMax M2.7"],
          ].map(([key, label, _default]) => (
            <FieldRow key={key} label={label}>
              <select
                value={(settings as any)[key] || _default}
                onChange={(e: any) => update(key, e.target.value)}
                style={{
                  background: "var(--bg)",
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  padding: "6px 10px",
                  color: "var(--text)",
                  fontSize: 13,
                  width: "100%",
                  maxWidth: 280,
                }}
              >
                <option>GLM-4.5-Air</option>
                <option>GLM-4.7</option>
                <option>GLM-5.1</option>
                <option>MiniMax-Text-01</option>
                <option>MiniMax M2.7</option>
              </select>
            </FieldRow>
          ))}
        </div>
      </Section>

      <Section title="Daily Budget">
        <FieldRow label="Max Spend / Day">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "var(--text-muted)" }}>₹</span>
            <Input type="number" value={settings.aiDailyBudget || "100"} onChange={(e: any) => update("aiDailyBudget", e.target.value)} placeholder="100" style={{ maxWidth: 120 }} />
          </div>
        </FieldRow>
        <FieldRow label="Spent Today">
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>₹ 0.00</span>
        </FieldRow>
        <FieldRow label="Auto-pause when budget hit">
          <Toggle checked={!!settings.aiBudgetPause} onChange={(v: boolean) => update("aiBudgetPause", v)} />
        </FieldRow>
      </Section>
    </div>
  );
}

function RiskTab({ settings, setSettings }: {
  settings: Settings;
  setSettings: (s: Settings) => void;
}) {
  const update = (key: string, value: unknown) => setSettings({ ...settings, [key]: value });

  return (
    <div>
      <Section title="Guardrails">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px" }}>
          <FieldRow label="Max Position Size">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input type="number" value={settings.guardMaxPosSize || "10"} onChange={(e: any) => update("guardMaxPosSize", e.target.value)} style={{ maxWidth: 80 }} />
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>% NAV</span>
            </div>
          </FieldRow>
          <FieldRow label="Sector Concentration">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input type="number" value={settings.guardSectorConcentration || "25"} onChange={(e: any) => update("guardSectorConcentration", e.target.value)} style={{ maxWidth: 80 }} />
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>%</span>
            </div>
          </FieldRow>
          <FieldRow label="Daily Loss Limit">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input type="number" value={settings.guardDailyLoss || "3"} onChange={(e: any) => update("guardDailyLoss", e.target.value)} style={{ maxWidth: 80 }} />
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>% NAV</span>
            </div>
          </FieldRow>
          <FieldRow label="Weekly Loss Limit">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input type="number" value={settings.guardWeeklyLoss || "6"} onChange={(e: any) => update("guardWeeklyLoss", e.target.value)} style={{ maxWidth: 80 }} />
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>% NAV</span>
            </div>
          </FieldRow>
          <FieldRow label="Max Drawdown Trigger">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input type="number" value={settings.guardMaxDrawdown || "8"} onChange={(e: any) => update("guardMaxDrawdown", e.target.value)} style={{ maxWidth: 80 }} />
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>% NAV</span>
            </div>
          </FieldRow>
          <FieldRow label="Consecutive Losses Halt">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input type="number" value={settings.guardConsecutiveLosses || "5"} onChange={(e: any) => update("guardConsecutiveLosses", e.target.value)} style={{ maxWidth: 80 }} />
              <span style={{ color: "var(--text-muted)", fontSize: 13 }}>trades</span>
            </div>
          </FieldRow>
        </div>
      </Section>

      <Section title="Position Sizing">
        <FieldRow label="Risk Per Trade">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Input type="number" value={settings.sizingRiskPerTrade || "1"} onChange={(e: any) => update("sizingRiskPerTrade", e.target.value)} style={{ maxWidth: 80 }} />
            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>% NAV</span>
          </div>
        </FieldRow>
        <FieldRow label="Stop Loss Type">
          <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {[
              { value: "fixed", label: "Fixed %" },
              { value: "atr", label: "2x ATR" },
              { value: "dma", label: "50 DMA" },
            ].map(opt => (
              <label key={opt.value} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                <input
                  type="radio"
                  name="slType"
                  value={opt.value}
                  checked={settings.sizingStopLossType === opt.value || (!settings.sizingStopLossType && opt.value === "atr")}
                  onChange={() => update("sizingStopLossType", opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </FieldRow>
        <FieldRow label="Default Target">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Input type="number" value={settings.sizingDefaultTarget || "3"} onChange={(e: any) => update("sizingDefaultTarget", e.target.value)} style={{ maxWidth: 80 }} />
            <span style={{ color: "var(--text-muted)", fontSize: 13 }}>R (risk multiples)</span>
          </div>
        </FieldRow>
      </Section>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const TABS: { key: Tab; label: string }[] = [
  { key: "connections", label: "Connections" },
  { key: "notifications", label: "Notifications" },
  { key: "mode", label: "Mode" },
  { key: "ai", label: "AI" },
  { key: "risk", label: "Risk" },
];

const defaults: Settings = {
  broker: "zerodha",
  apiKey: "",
  apiSecret: "",
  totpSecret: "",
  defaultMode: "VIRTUAL",
  mode: "virtual",
  maxPositionSize: "1000000",
  stopLossPct: "2",
  riskPerTrade: "1",
  autoTrade: false,
  notifications: true,
  slackWebhook: "",
  telegramBotToken: "",
  telegramChatId: "",
  zaiApiKey: "",
  minimaxApiKey: "",
  aiEnabled: true,
  aiConfirmOrders: true,
  aiVetoSignals: true,
  aiNewsSentiment: false,
  aiWeeklyReview: false,
  aiMonthlyPatches: false,
  aiAutoSize: false,
  aiModelTriage: "GLM-4.5-Air",
  aiModelReasoning: "GLM-4.7",
  aiModelVeto: "GLM-5.1",
  aiModelBulk: "MiniMax M2.7",
  aiDailyBudget: "100",
  aiBudgetPause: true,
  guardMaxPosSize: "10",
  guardSectorConcentration: "25",
  guardDailyLoss: "3",
  guardWeeklyLoss: "6",
  guardMaxDrawdown: "8",
  guardConsecutiveLosses: "5",
  sizingRiskPerTrade: "1",
  sizingStopLossType: "atr",
  sizingDefaultTarget: "3",
  notificationEvents: {
    trade_placed: true,
    trade_filled: true,
    stop_loss_hit: true,
    target_hit: true,
    guardrail_breach: true,
    autohalt_triggered: true,
    daily_pnl: true,
    weekly_review: false,
    ai_veto: true,
    routine_failed: true,
  },
};

export default function Settings() {
  const [activeTab, setActiveTab] = useState<Tab>("connections");
  const [settings, setSettings] = useState<Settings>(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, TestResult>>({});
  const [testingKey, setTestingKey] = useState("");
  const [showTotp, setShowTotp] = useState(false);
  const qc = useQueryClient();

  // Load settings
  useEffect(() => {
    api.get<{ data: Settings }>("/settings")
      .then(({ data }) => {
        const s = data as Partial<Settings>;
        setSettings({
          ...defaults,
          ...s,
          // merge notificationEvents
          notificationEvents: { ...defaults.notificationEvents, ...(s.notificationEvents || {}) },
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveSettings = useCallback(async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/settings", settings);
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["settings"] });
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      setSaving(false);
    }
  }, [settings, qc]);

  const runTest = useCallback(async (key: string, endpoint: string) => {
    setTestingKey(key);
    setTestResults(prev => ({ ...prev, [key]: { success: false, message: "Testing..." } }));
    try {
      // Send relevant credentials from current settings
      const payload: Record<string, string> = {};
      if (key === "zerodha") {
        payload.apiKey = settings.apiKey || "";
        payload.apiSecret = settings.apiSecret || "";
        payload.totpSecret = settings.totpSecret || "";
      } else if (key === "zai") {
        payload.apiKey = settings.zaiApiKey || "";
      } else if (key === "minimax") {
        payload.apiKey = settings.minimaxApiKey || "";
      } else if (key === "telegram") {
        payload.botToken = settings.telegramBotToken || "";
        payload.chatId = settings.telegramChatId || "";
      } else if (key === "slack") {
        payload.webhook = settings.slackWebhook || "";
      }
      const result = await api.post<{ success: boolean; message: string }>(endpoint, payload);
      setTestResults(prev => ({ ...prev, [key]: { success: result.success, message: result.message } }));
    } catch (e: any) {
      setTestResults(prev => ({ ...prev, [key]: { success: false, message: e.message || "Test failed" } }));
    } finally {
      setTestingKey("");
    }
  }, [settings]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 200 }}>
        <span style={{ color: "var(--text-muted)" }}>Loading settings...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Settings</h1>
        <SaveButton onClick={saveSettings} loading={saving} saved={saved} />
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border)", marginBottom: 24 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 20px",
              background: "transparent",
              border: "none",
              borderBottom: activeTab === tab.key ? "2px solid var(--primary-light)" : "2px solid transparent",
              color: activeTab === tab.key ? "var(--primary-light)" : "var(--text-muted)",
              fontSize: 14,
              fontWeight: activeTab === tab.key ? 600 : 400,
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ maxWidth: 800 }}>
        {activeTab === "connections" && (
          <ConnectionsTab settings={settings} setSettings={setSettings} testResults={testResults} runTest={runTest} />
        )}
        {activeTab === "notifications" && (
          <NotificationsTab settings={settings} setSettings={setSettings} testResults={testResults} runTest={runTest} />
        )}
        {activeTab === "mode" && (
          <ModeTab settings={settings} setSettings={setSettings} showTotp={showTotp} setShowTotp={setShowTotp} />
        )}
        {activeTab === "ai" && (
          <AITab settings={settings} setSettings={setSettings} />
        )}
        {activeTab === "risk" && (
          <RiskTab settings={settings} setSettings={setSettings} />
        )}
      </div>

      {/* Footer save */}
      <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end" }}>
        <SaveButton onClick={saveSettings} loading={saving} saved={saved} />
      </div>
    </div>
  );
}
